'use client';

import { Chevron } from '@/assets/Chevron';
import { buildBlogListQueryString } from '@/components/blog-list/blogListParams';
import clsx from 'clsx';
import { startTransition } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

interface IBlogPaginationControlsProps {
  currentPage: number;
  totalPages: number;
}

function getVisiblePages(currentPage: number, totalPages: number) {
  const pageNumbers = new Set<number>([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);

  return Array.from(pageNumbers)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((firstPage, secondPage) => firstPage - secondPage);
}

export default function BlogPaginationControls(props: IBlogPaginationControlsProps) {
  const { currentPage, totalPages } = props;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  if (totalPages <= 1) return null;

  const visiblePages = getVisiblePages(currentPage, totalPages);

  function goToPage(page: number) {
    const nextQueryString = buildBlogListQueryString(new URLSearchParams(searchParams.toString()), { page });
    const nextUrl = nextQueryString ? `${pathname}?${nextQueryString}` : pathname;

    startTransition(() => {
      router.push(nextUrl, { scroll: false });
    });
  }

  return (
    <div className='mt-10 flex items-center justify-center gap-2'>
      <button
        aria-label='Previous page'
        className='border-primary-aqua/55 inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border disabled:cursor-not-allowed disabled:opacity-40 hover:bg-primary-aqua/10 disabled:hover:bg-transparent'
        disabled={currentPage === 1}
        type='button'
        onClick={() => goToPage(currentPage - 1)}
      >
        <Chevron height={12} rotate width={12} />
      </button>

      {visiblePages.map((page, index) => (
        <div className='flex items-center gap-2' key={page}>
          {index > 0 && visiblePages[index - 1] !== page - 1 && <span className='px-1 text-sm'>...</span>}
          <button
            aria-current={currentPage === page ? 'page' : undefined}
            className={clsx(
              'inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border text-sm transition-colors',
              currentPage === page
                ? 'border-primary-aqua bg-primary-aqua text-primary-black'
                : 'border-primary-aqua/55 text-primary-white hover:bg-primary-aqua/10'
            )}
            type='button'
            onClick={() => goToPage(page)}
          >
            {page}
          </button>
        </div>
      ))}

      <button
        aria-label='Next page'
        className='border-primary-aqua/55 inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border disabled:cursor-not-allowed disabled:opacity-40'
        disabled={currentPage === totalPages}
        type='button'
        onClick={() => goToPage(currentPage + 1)}
      >
        <Chevron height={12} width={12} />
      </button>
    </div>
  );
}
